<template>
  <div
    v-if="$vuetify.breakpoint.xs && !isVideoMode"
    class="bottom-mini-player"
    :class="{
      'dark-theme': $vuetify.theme.dark,
      'light-theme': !$vuetify.theme.dark
    }"
  >
    <!-- AUDIO EFFECTS BUTTON (top right) -->
    <v-btn
      v-if="isLoggedIn && isMasterPlaylist"
      @click="openAudioEffectsDialog"
      fab
      x-small
      :outlined="hasActiveEffects"
      :class="['audio-effects-button-mobile', { 'fx-active': hasActiveEffects }]"
    >
      <span class="fx-text">FX</span>
    </v-btn>

    <!-- TRACK TITLE -->
    <div class="mini-player-track-title text--primary prevent-select">
      {{ currentTrackName }}
    </div>

    <!-- PROGRESS BAR WITH TIMES -->
    <div class="mini-player-progress-container">
      <span class="time-text caption text--secondary prevent-select">{{ currentPosition }}</span>

      <div
        class="progress-slider-wrapper"
        @mousedown="progressSliderTapped"
        @touchstart="progressSliderTapped"
      >
        <waveform-canvas
          v-if="currentTrackDetails"
          :track-name="currentTrackDetails.name"
          :duration="currentTrackDetails.duration"
        />
        <v-slider
          v-model="trackProgress"
          @change="userClickedOnProgressSlider"
          @mousedown="progressSliderTapped"
          @touchmove="progressSliderTapped"
          @mouseup="progressSliderReleased"
          @touchend="progressSliderReleased"
          color="primary"
          track-color="tertiary"
          hide-details
          :step="0"
          min="0"
          max="100"
          dense
        />
      </div>

      <span class="time-text caption text--secondary prevent-select">{{ duration }}</span>
    </div>

    <!-- CONTROL BUTTONS -->
    <div class="mini-player-controls prevent-select">
      <!-- SHUFFLE -->
      <v-btn
        @click="toggleShuffle"
        icon
        small
        color="secondary"
        :outlined="isShuffleMode"
        class="control-button"
      >
        <v-icon small>mdi-shuffle</v-icon>
      </v-btn>

      <!-- PREVIOUS -->
      <v-btn
        @click="previousTrack"
        icon
        color="secondary"
        class="control-button"
      >
        <v-icon>mdi-skip-previous</v-icon>
      </v-btn>

      <!-- PLAY/PAUSE -->
      <v-btn
        @click="togglePlayPause"
        icon
        large
        color="secondary"
        class="control-button play-button"
      >
        <transition name="spin-fade" mode="out-in">
          <v-progress-circular
            v-if="showLoadingSpinner"
            key="spinner"
            indeterminate
            color="primary"
            :size="24"
            :width="2.5"
          />
          <v-icon v-else key="icon" large>{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
        </transition>
      </v-btn>

      <!-- NEXT -->
      <v-btn
        @click="nextTrack"
        icon
        color="secondary"
        class="control-button"
      >
        <v-icon>mdi-skip-next</v-icon>
      </v-btn>

      <!-- REPEAT -->
      <v-btn
        @click="toggleRepeat"
        icon
        small
        color="secondary"
        :outlined="repeatMode > 0"
        class="control-button"
      >
        <v-icon small>{{ repeatIcon }}</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AudioService from '@/services/AudioService';
import WaveformCanvas from '@/components/player/WaveformCanvas.vue';

export default {
  name: 'BottomMiniPlayer',
  components: { WaveformCanvas },

  data() {
    return {
      trackProgress: 0,
      currentPosition: '00:00',
      duration: '00:00',
      progressAnimationFrame: null,
      shouldPreventTooManyClicks: false,
      isUserInteractingWithSlider: false,
      showLoadingSpinner: false,
      loadingSpinnerTimeout: null,
    };
  },

  watch: {
    isTrackLoading(loading) {
      if (loading) {
        this.loadingSpinnerTimeout = setTimeout(() => {
          this.showLoadingSpinner = true;
        }, 500);
      } else {
        clearTimeout(this.loadingSpinnerTimeout);
        this.showLoadingSpinner = false;
        this.updateTrackProgress();
      }
    },

    // The progress loop only runs while audio is playing — a paused player
    // must not keep a 60 fps rAF loop alive (constant CPU wakeups on
    // battery-powered devices).
    isPlaying(playing) {
      if (playing && this._startProgressLoop) {
        this._startProgressLoop();
      } else if (!playing) {
        // One final sync so the paused position is accurate
        this.updateTrackProgress();
      }
    },
  },

  computed: {
    ...mapGetters({
      audioTrackDetails: 'player/currentTrackDetails',
      audioIsPlaying: 'player/isPlaying',
      audioIsShuffleMode: 'player/isShuffleMode',
      audioRepeatMode: 'player/repeatMode',
      audioRepeatIcon: 'player/repeatIcon',
      isVideoMode: 'ui/isVideoMode',
      audioIsTrackLoading: 'player/isTrackLoading',
      hasActiveEffects: 'audioEffects/hasActiveEffects',
      isLoggedIn: 'auth/isLoggedIn',
      playlistURL: 'playlist/playlistURL',
    }),

    isMasterPlaylist() {
      return this.playlistURL === null;
    },

    currentTrackDetails() {
      return this.audioTrackDetails;
    },

    isPlaying() {
      return this.audioIsPlaying;
    },

    isShuffleMode() {
      return this.audioIsShuffleMode;
    },

    repeatMode() {
      return this.audioRepeatMode;
    },

    repeatIcon() {
      return this.audioRepeatIcon;
    },

    currentTrackName() {
      return this.currentTrackDetails ? this.currentTrackDetails.name : ' ';
    },

    isTrackLoading() {
      return this.audioIsTrackLoading;
    },
  },

  mounted() {
    // Progress updates via rAF, but:
    // - the loop only runs while playing (see isPlaying watcher) instead of
    //   spinning at 60 fps forever, and
    // - actual reads/DOM updates are throttled to ~4x/second — plenty for a
    //   thin progress bar, and far cheaper than per-frame Vuetify slider
    //   re-renders on an older phone.
    const PROGRESS_UPDATE_INTERVAL_MS = 250;
    let lastUpdate = 0;

    const updateLoop = (timestamp) => {
      this.progressAnimationFrame = null;
      if (!this.isPlaying) {
        return; // loop parks; the isPlaying watcher restarts it
      }
      if (
        !this.isUserInteractingWithSlider &&
        timestamp - lastUpdate >= PROGRESS_UPDATE_INTERVAL_MS
      ) {
        lastUpdate = timestamp;
        this.updateTrackProgress();
      }
      this.progressAnimationFrame = requestAnimationFrame(updateLoop);
    };

    this._startProgressLoop = () => {
      if (this.progressAnimationFrame) return;
      this.progressAnimationFrame = requestAnimationFrame(updateLoop);
    };

    if (this.isPlaying) {
      this._startProgressLoop();
    }
    this.updateTrackProgress();
  },

  beforeDestroy() {
    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame);
    }
    clearTimeout(this.loadingSpinnerTimeout);
  },

  methods: {
    ...mapActions({
      toggleAudioPlayPause: 'player/togglePlayPause',
      previousAudioTrack: 'player/previousTrack',
      nextAudioTrack: 'player/nextTrack',
      toggleAudioShuffle: 'player/toggleShuffle',
      toggleAudioRepeat: 'player/toggleRepeat',
      seekAudioToPosition: 'player/seekToPosition',
      startHoldingAudioSlider: 'player/startHoldingProgressSlider',
      stopHoldingAudioSlider: 'player/stopHoldingProgressSlider',
    }),

    togglePlayPause() {
      if (this.shouldPreventTooManyClicks) return;
      this.toggleAudioPlayPause();

      this.shouldPreventTooManyClicks = true;
      setTimeout(() => {
        this.shouldPreventTooManyClicks = false;
      }, 500);
    },

    previousTrack() {
      if (this.shouldPreventTooManyClicks) return;
      this.previousAudioTrack();

      this.shouldPreventTooManyClicks = true;
      setTimeout(() => {
        this.shouldPreventTooManyClicks = false;
      }, 500);
    },

    nextTrack() {
      if (this.shouldPreventTooManyClicks) return;
      this.nextAudioTrack();

      this.shouldPreventTooManyClicks = true;
      setTimeout(() => {
        this.shouldPreventTooManyClicks = false;
      }, 500);
    },

    toggleShuffle() {
      this.toggleAudioShuffle();
    },

    toggleRepeat() {
      this.toggleAudioRepeat();
    },

    openAudioEffectsDialog() {
      this.$store.dispatch('audioEffects/openDialog');
    },

    updateTrackProgress() {
      if (!this.currentTrackDetails) {
        return;
      }

      const progress = AudioService.getTrackProgress();

      if (progress) {
        const newProgress = progress.progressPercent || 0;
        const newPosition = progress.currentPosition || '00:00';
        const newDuration = progress.duration || this.currentTrackDetails.duration || '00:00';

        // Only update slider if change is significant (reduces unnecessary reactivity)
        // 0.15% of a track is sub-pixel on a phone-width slider, so this
        // stays visually smooth while cutting re-renders to a few per second
        if (Math.abs(this.trackProgress - newProgress) > 0.15) {
          this.trackProgress = newProgress;
        }

        // Only update time displays if they've actually changed
        if (this.currentPosition !== newPosition) {
          this.currentPosition = newPosition;
        }
        if (this.duration !== newDuration) {
          this.duration = newDuration;
        }
      }
    },

    progressSliderTapped() {
      this.isUserInteractingWithSlider = true;
      this.startHoldingAudioSlider();
    },

    progressSliderReleased() {
      this.isUserInteractingWithSlider = false;
      this.stopHoldingAudioSlider();
    },

    userClickedOnProgressSlider(newValue) {
      this.seekAudioToPosition(newValue);
      // While paused the progress loop is parked — sync the display once
      // after the seek settles
      setTimeout(() => this.updateTrackProgress(), 150);
    },
  },
};
</script>

<style scoped>
.bottom-mini-player {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 16px 4px 16px;
  padding-bottom: calc(4px + env(safe-area-inset-bottom)); /* iOS safe area */
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
}

.audio-effects-button-mobile {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 10;
}

/* Glowing animation when effects are active */
.fx-active {
  animation: fx-glow 3s ease-in-out infinite;
}

@keyframes fx-glow {
  0%, 100% {
    background-color: transparent;
  }
  50% {
    background-color: rgba(160, 160, 160, 0.3);
  }
}

.fx-text {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* NOTE: deliberately no backdrop-filter here. A full-width blur layer is one
   of the most expensive compositor effects on older iOS GPUs — it re-blurs
   on every repaint (all scrolling + every progress update). Near-opaque
   solid backgrounds read almost identically at a fraction of the cost. */
.dark-theme.bottom-mini-player {
  background: rgba(18, 18, 18, 0.98);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.light-theme.bottom-mini-player {
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

.mini-player-track-title {
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 6px;
  line-height: 1.2;
  text-align: center;
}

.mini-player-progress-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0px;
}

.time-text {
  font-size: 11px;
  min-width: 32px;
  text-align: center;
}

.progress-slider-wrapper {
  position: relative;
  flex: 1;
  min-width: 0; /* Allow flex item to shrink */
}

.progress-slider-wrapper >>> .waveform-canvas {
  height: 30px;
}

.mini-player-controls {
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 4px;
}

.control-button {
  min-width: 48px !important;
  min-height: 48px !important;
  width: 48px !important;
  height: 48px !important;
}

.play-button {
  min-width: 56px !important;
  min-height: 56px !important;
  width: 56px !important;
  height: 56px !important;
}

/* Ensure slider doesn't have extra padding on mobile */
.mini-player-progress-container >>> .v-slider {
  margin: 0;
  padding: 0;
}

.mini-player-progress-container >>> .v-slider__track-container {
  height: 4px;
}

.mini-player-progress-container >>> .v-slider__thumb {
  width: 16px;
  height: 16px;
}

/* Fix Vuetify slider focus ripple offset on touch devices (iOS Safari) */
.mini-player-progress-container >>> .v-slider__thumb::before {
  display: none;
}

/* Prevent scroll interference during slider interaction on iOS Safari */
.progress-slider-wrapper {
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}

/* Smaller screens: tighter spacing and smaller fonts */
@media only screen and (max-width: 450px) {
  .bottom-mini-player {
    padding: 10px 10px 2px 10px;
    padding-bottom: calc(2px + env(safe-area-inset-bottom));
  }

  .audio-effects-button-mobile {
    top: 2px;
    right: 2px;
  }

  .mini-player-track-title {
    font-size: 14px;
    margin-bottom: 4px;
  }

  .mini-player-progress-container {
    gap: 8px;
    margin-bottom: 0px;
  }

  .time-text {
    font-size: 10px;
    min-width: 30px;
  }

  .control-button {
    min-width: 44px !important;
    min-height: 44px !important;
    width: 44px !important;
    height: 44px !important;
  }

  .play-button {
    min-width: 52px !important;
    min-height: 52px !important;
    width: 52px !important;
    height: 52px !important;
  }
}

/* Play button loading spinner crossfade */
.spin-fade-enter-active, .spin-fade-leave-active {
  transition: opacity 0.2s ease;
}
.spin-fade-enter, .spin-fade-leave-to {
  opacity: 0;
}
</style>
